import { useQuery } from '@tanstack/react-query';

import { Role, UserProfile } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { createLogger } from '~/utils/logging';
import { AuthenticationError } from '~/errors';
import { sanitizeTagValue } from '~/utils/instrumentation';

const log = createLogger('useUserProfileQuery');

export const QUERY_KEY = 'userProfile';

export type UserProfileRequest = {
  token: string;
};

export type UserProfileResponse = {
  user: UserProfile;
  activeRole: Role;
  availableRoles: Role[];
};

export function useUserProfile() {
  const client = useHttpClient();

  const { data, isPending, isFetching, error } = useQuery<null | UserProfileResponse>({
    queryKey: [QUERY_KEY],
    enabled: client.isAuthenticated,
    queryFn: async () => {
      log.debug('Fetching user profile');
      const response = await client.get<UserProfileResponse>('/user/getProfile');
      log.info('User profile successfully fetched', {
        userId: response.user.id,
        userIsSetupComplete: response.user.isSetupComplete,
        userIsSpark: response.user.isSpark,
        activeRole: response.activeRole,
      });
      return response;
    },
    retryOnMount: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) {
        return false;
      }
      log.warn('Failed to fetch user profile, retrying', {
        errorMessage: sanitizeTagValue(error.message),
        failureCount,
      });
      return failureCount < 3;
    },
  });

  let isAuthenticated;
  if (error && error instanceof AuthenticationError) {
    isAuthenticated = false;
  } else if (data?.user) {
    isAuthenticated = true;
  } else {
    isAuthenticated = false;
  }

  return {
    // Make sure we never return a stale `user` object if the user's session has expired.
    user: isAuthenticated ? data?.user : undefined,
    activeRole: isAuthenticated ? data?.activeRole : undefined,
    availableRoles: isAuthenticated ? data?.availableRoles : undefined,
    isPending,
    isLoading: isPending && isFetching,
    error,
  };
}
